import { RRule } from "rrule";
import ICAL from "ical.js";

// ------------------------------------------------------------
// Basics
// ------------------------------------------------------------
export function generateUid() {
  return `${Math.random().toString(36).slice(2)}@contentplanner`;
}

export function addMinutes(d, mins) {
  const x = new Date(d);
  x.setMinutes(x.getMinutes() + mins);
  return x;
}

export function floorTo15(d) {
  const x = new Date(d);
  x.setSeconds(0, 0);
  x.setMinutes(Math.floor(x.getMinutes() / 15) * 15);
  return x;
}

export function roundTo15(d) {
  const x = new Date(d);
  x.setSeconds(0, 0);
  const m = x.getMinutes();
  const rounded = Math.round(m / 15) * 15;
  x.setMinutes(rounded === 60 ? 0 : rounded);
  if (rounded === 60) x.setHours(x.getHours() + 1);
  return x;
}

export function stripTime(d) {
  const x = new Date(d);
  x.setHours(0, 0, 0, 0);
  return x;
}

export function combineDateAndTime(dateOnly, timeOnly) {
  const d = new Date(dateOnly);
  const t = new Date(timeOnly);
  d.setHours(t.getHours(), t.getMinutes(), 0, 0);
  return d;
}

export function sameDate(a, b) {
  return (
    a instanceof Date &&
    b instanceof Date &&
    a.getFullYear() === b.getFullYear() &&
    a.getMonth() === b.getMonth() &&
    a.getDate() === b.getDate()
  );
}

export function clampInt(v, min, max) {
  if (!Number.isFinite(v)) return min;
  return Math.max(min, Math.min(max, Math.trunc(v)));
}

// keep an event inside the day of "start" (23:59 max) and min 15min
export function clampEndSameDay(start, end) {
  const s = new Date(start);
  let e = new Date(end);

  const eod = new Date(s.getFullYear(), s.getMonth(), s.getDate(), 23, 59, 0, 0);

  if (e > eod) e = eod;
  if (e <= s) e = new Date(s.getTime() + 15 * 60 * 1000);
  if (e > eod) e = eod;

  return e;
}

// ------------------------------------------------------------
// RRULE helpers (editor uses these)
// ------------------------------------------------------------
export function freqStringToNumber(freq) {
  const f = String(freq || "").toUpperCase();
  switch (f) {
    case "DAILY":
      return RRule.DAILY;
    case "WEEKLY":
      return RRule.WEEKLY;
    case "MONTHLY":
      return RRule.MONTHLY;
    case "YEARLY":
      return RRule.YEARLY;
    default:
      return null;
  }
}

export function freqNumberToString(freqNum) {
  switch (freqNum) {
    case RRule.DAILY:
      return "DAILY";
    case RRule.WEEKLY:
      return "WEEKLY";
    case RRule.MONTHLY:
      return "MONTHLY";
    case RRule.YEARLY:
      return "YEARLY";
    default:
      return "DAILY";
  }
}

export function tokenToWeekday(tok) {
  switch (tok) {
    case "MO":
      return RRule.MO;
    case "TU":
      return RRule.TU;
    case "WE":
      return RRule.WE;
    case "TH":
      return RRule.TH;
    case "FR":
      return RRule.FR;
    case "SA":
      return RRule.SA;
    case "SU":
      return RRule.SU;
    default:
      return null;
  }
}

export function weekdayToToken(weekdayObj) {
  const idx = weekdayObj?.weekday;
  return ["MO", "TU", "WE", "TH", "FR", "SA", "SU"][idx] || "MO";
}

export function tokenToShort(tok) {
  return (
    {
      MO: "mon",
      TU: "tue",
      WE: "wed",
      TH: "thu",
      FR: "fri",
      SA: "sat",
      SU: "sun",
    }[tok] || tok
  );
}

export function sortWeekdayTokens(tokens) {
  const order = { MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6, SU: 7 };
  return (tokens || [])
    .slice()
    .sort((a, b) => (order[a] || 999) - (order[b] || 999));
}

export function dayToToken(jsDay) {
  return ["SU", "MO", "TU", "WE", "TH", "FR", "SA"][jsDay] || "MO";
}

export function alignDateToNextWeekday(baseDate, weekdayToken) {
  const map = { SU: 0, MO: 1, TU: 2, WE: 3, TH: 4, FR: 5, SA: 6 };
  const target = map[weekdayToken];
  if (target == null) return new Date(baseDate);

  const d = new Date(baseDate);
  const delta = (target - d.getDay() + 7) % 7;
  d.setDate(d.getDate() + delta);
  d.setHours(0, 0, 0, 0);
  return d;
}

export function nthToLabel(n) {
  switch (n) {
    case 1:
      return "1st";
    case 2:
      return "2nd";
    case 3:
      return "3rd";
    case 4:
      return "4th";
    case -1:
      return "last";
    default:
      return String(n);
  }
}

export function formatMonthDay(d) {
  if (!(d instanceof Date)) return "";
  const mm = String(d.getMonth() + 1).padStart(2, "0");
  const dd = String(d.getDate()).padStart(2, "0");
  return `${dd}/${mm}`;
}

export function formatHHMM(d) {
  const hh = String(d.getHours()).padStart(2, "0");
  const mm = String(d.getMinutes()).padStart(2, "0");
  return `${hh}:${mm}`;
}

// ------------------------------------------------------------
// Local timezone identity (browser)
// ------------------------------------------------------------
export function getLocalTzid(fallback = "UTC") {
  try {
    return Intl.DateTimeFormat().resolvedOptions().timeZone || fallback;
  } catch {
    return fallback;
  }
}

// ------------------------------------------------------------
// ICS date formatting & parsing (FLOATING LOCAL)
// ------------------------------------------------------------
export function pad2(n) {
  return String(n).padStart(2, "0");
}

// Build "floating local" string: YYYYMMDDTHHMMSS (no Z)
export function toIcalLocal(dt) {
  return (
    dt.getFullYear() +
    pad2(dt.getMonth() + 1) +
    pad2(dt.getDate()) +
    "T" +
    pad2(dt.getHours()) +
    pad2(dt.getMinutes()) +
    "00"
  );
}

// If you still need ICAL.Time objects for editor helpers (rare)
export function toIcalTime(date) {
  const d = date instanceof Date ? date : new Date(date);
  const t = new ICAL.Time();
  t.year = d.getFullYear();
  t.month = d.getMonth() + 1;
  t.day = d.getDate();
  t.hour = d.getHours();
  t.minute = d.getMinutes();
  t.second = 0;
  t.isDate = false;
  return t;
}

// ------------------------------------------------------------
// ICS builders
// ------------------------------------------------------------
export function buildInitialIcsFromDrop({ title, start, end }) {
  const toLocal = (d) =>
    d.getFullYear() +
    pad2(d.getMonth() + 1) +
    pad2(d.getDate()) +
    "T" +
    pad2(d.getHours()) +
    pad2(d.getMinutes()) +
    "00";

  return [
    "BEGIN:VCALENDAR",
    "VERSION:2.0",
    "PRODID:-//elevator.tv//contentplanner//EN",
    "BEGIN:VEVENT",
    `UID:drop-${Date.now()}@elevator.tv`,
    `DTSTAMP:${toLocal(new Date())}`,
    `SUMMARY:${title}`,
    `DTSTART:${toLocal(start)}`,
    `DTEND:${toLocal(end)}`,
    "END:VEVENT",
    "END:VCALENDAR",
  ].join("\r\n");
}

// ------------------------------------------------------------
// iCal parsing helpers
// ------------------------------------------------------------

// Helper: unfold folded iCal lines (RFC 5545)
export function unfoldIcal(s) {
  return String(s || "").replace(/\r?\n[ \t]/g, "");
}

// Helper: extract DTSTART/DTEND as a "YYYYMMDDTHHMMSS" string (no TZ handling here; local wall-time)
export function getPropValueAsIcalText(prop) {
  if (!prop) return "";

  // If ical.js already parsed to ICAL.Time:
  const v = prop.getFirstValue?.();
  if (v && typeof v.toString === "function") {
    if (typeof v.year === "number") {
      return (
        String(v.year) +
        pad2(v.month || 1) +
        pad2(v.day || 1) +
        "T" +
        pad2(v.hour || 0) +
        pad2(v.minute || 0) +
        pad2(v.second || 0)
      );
    }
  }

  // Fallback: parse raw property line "DTSTART...:20260220T080000"
  const raw = unfoldIcal(prop.toString?.() || "");
  const idx = raw.indexOf(":");
  if (idx < 0) return "";
  return raw.substring(idx + 1).trim();
}

// Helper: parse iCal "basic" datetime into JS Date in *browser local timezone*
// NOTE: this treats values WITHOUT 'Z' as "floating local" (your DB format).
export function parseIcalLocalDateTime(txt) {
  const s = String(txt || "").trim();
  if (!s) return null;

  // Date-only (YYYYMMDD)
  if (/^\d{8}$/.test(s)) {
    const y = Number(s.slice(0, 4));
    const mo = Number(s.slice(4, 6));
    const d = Number(s.slice(6, 8));
    return new Date(y, mo - 1, d, 0, 0, 0, 0);
  }

  // Date-time (YYYYMMDDTHHMMSS or YYYYMMDDTHHMM)
  const m = s.match(/^(\d{4})(\d{2})(\d{2})T(\d{2})(\d{2})(\d{2})?$/);
  if (!m) return null;

  const y = Number(m[1]);
  const mo = Number(m[2]);
  const d = Number(m[3]);
  const hh = Number(m[4]);
  const mm = Number(m[5]);
  const ss = Number(m[6] || "0");
  return new Date(y, mo - 1, d, hh, mm, ss, 0);
}

// ------------------------------------------------------------
// "Floating UTC" conversions for RRULE math
// ------------------------------------------------------------

// Keep local wall-clock components, but store them as UTC (prevents timezone shifts in RRULE math)
export function toFloatingUtcDate(localDate) {
  if (!localDate) return null;
  return new Date(
    Date.UTC(
      localDate.getFullYear(),
      localDate.getMonth(),
      localDate.getDate(),
      localDate.getHours(),
      localDate.getMinutes(),
      localDate.getSeconds() || 0,
      localDate.getMilliseconds() || 0
    )
  );
}

// Interpret UTC fields as local wall-clock (used to display RRULE occurrences)
export function fromFloatingUtcDate(utcDate) {
  if (!utcDate) return null;
  const d = new Date(utcDate);
  return new Date(
    d.getUTCFullYear(),
    d.getUTCMonth(),
    d.getUTCDate(),
    d.getUTCHours(),
    d.getUTCMinutes(),
    d.getUTCSeconds(),
    d.getUTCMilliseconds()
  );
}

// ------------------------------------------------------------
// Accepts: RRule.Weekday object, number, "MO" string, or array of those.
// ------------------------------------------------------------
export function normalizeByweekday(raw) {
  if (!raw) return [];
  const WG = ["MO", "TU", "WE", "TH", "FR", "SA", "SU"];

  const conv = (w) => {
    if (!w && w !== 0) return null;

    if (typeof w === "object" && typeof w.weekday === "number") {
      return WG[w.weekday] || null;
    }
    if (typeof w === "number") {
      return WG[w] || null;
    }
    if (typeof w === "string") {
      const m = (w || "").toUpperCase().match(/\b(MO|TU|WE|TH|FR|SA|SU)\b/);
      return m ? m[1] : null;
    }
    try {
      const s = String(w || "");
      const m = s.toUpperCase().match(/\b(MO|TU|WE|TH|FR|SA|SU)\b/);
      return m ? m[1] : null;
    } catch {
      return null;
    }
  };

  if (Array.isArray(raw)) {
    const out = raw.map(conv).filter(Boolean);
    return Array.from(new Set(out)).sort((a, b) => WG.indexOf(a) - WG.indexOf(b));
  }

  const single = conv(raw);
  return single ? [single] : [];
}

// ------------------------------------------------------------
// Parse a single VEVENT for editor
// returns: { dtStart, dtEnd, rrule, rruleString, byWeekdayTokens, occurrenceToLocal }
// ------------------------------------------------------------
export function parseFirstVevent(icsText) {
  const jcal = ICAL.parse(String(icsText || ""));
  const vcal = new ICAL.Component(jcal);
  const vevent = vcal.getFirstSubcomponent("vevent");
  if (!vevent) return null;

  const dtStartText = getPropValueAsIcalText(vevent.getFirstProperty("dtstart"));
  const dtEndText = getPropValueAsIcalText(vevent.getFirstProperty("dtend"));

  // UI/editing dates: LOCAL
  const dtStart = parseIcalLocalDateTime(dtStartText);
  const dtEnd = parseIcalLocalDateTime(dtEndText);

  const rruleProp = vevent.getFirstProperty("rrule");
  let rrule = null;
  let rruleString = "";
  let byWeekdayTokens = [];

  // ✅ make sure this exists even if no rrule / parsing fails
  let monthly = null;

  if (rruleProp && dtStart) {
    const v = rruleProp.getFirstValue?.();
    if (v && typeof v.toString === "function") {
      rruleString = String(v.toString() || "").trim();
    } else {
      rruleString = unfoldIcal(rruleProp.toString?.() || "")
        .replace(/^RRULE:/i, "")
        .trim();
    }

    rruleString = rruleString.replace(/\s+/g, "");

    if (rruleString && /FREQ=/i.test(rruleString)) {
      try {
        const tmp = RRule.fromString(rruleString);

        byWeekdayTokens = normalizeByweekday(tmp?.options?.byweekday);

        // ✅ decode monthly pattern for editor re-open (optional, but useful)
        monthly = decodeMonthlyPatternFromRRule(tmp);

        // RRULE math: FLOATING-UTC anchor (prevents timezone jumping)
        const dtStartRrule = toFloatingUtcDate(dtStart);

        rrule = new RRule({
          ...tmp.options,
          dtstart: dtStartRrule,
        });
      } catch (e) {
        console.warn("bad RRULE in ICS, ignoring recurrence:", rruleString, e);
        rrule = null;
        rruleString = "";
        byWeekdayTokens = [];
        monthly = null;
      }
    }
  }

  return {
    dtStart,
    dtEnd,
    rrule,
    rruleString,
    byWeekdayTokens,
    monthly, 
    occurrenceToLocal: fromFloatingUtcDate,
  };
}

// ------------------------------------------------------------
// Parse for expansion (calendar rendering)
// returns: { dtStart, dtEnd, durationMs, rruleValue }
// Uses ical.js so DTSTART/DTEND reading is consistent with editor parsing.
// ------------------------------------------------------------
export function parseIcsForExpansion(ics) {
  const parsed = parseFirstVevent(ics);
  if (!parsed?.dtStart || !parsed?.dtEnd) return null;

  const durationMs = parsed.dtEnd.getTime() - parsed.dtStart.getTime();
  if (!Number.isFinite(durationMs) || durationMs <= 0) return null;

  const rruleValue = String(parsed.rruleString || "").trim();
  return { dtStart: parsed.dtStart, dtEnd: parsed.dtEnd, durationMs, rruleValue };
}

// ------------------------------------------------------------
// Expand schedules into display events for react-big-calendar
// ------------------------------------------------------------
export function expandSchedulesForRange(item, rangeStart, rangeEnd, opts = {}) {
  const { TYPE_COLORS = {}, safeType = (x) => x?.type || x?.contentType || "other" } = opts;

  const parsed = parseFirstVevent(item?.ics);
  if (!parsed?.dtStart || !parsed?.dtEnd) return [];

  const rs = rangeStart instanceof Date ? rangeStart : new Date(rangeStart);
  const re = rangeEnd instanceof Date ? rangeEnd : new Date(rangeEnd);
  if (isNaN(+rs) || isNaN(+re)) return [];

  const dtStartLocal = parsed.dtStart;
  const durationMs = Math.max(15 * 60 * 1000, parsed.dtEnd.getTime() - parsed.dtStart.getTime());
  const rruleString = String(parsed.rruleString || "").trim();

  const ctype = safeType(item) || "other";
  const color = TYPE_COLORS?.[ctype] ?? TYPE_COLORS?.other;

  // your micro-shift logic (kept)
  const rank = Number(item?.rank ?? 0);
  const shiftMs = 0;

  const buildEvent = (startBase, idSuffix, isRecurring) => {
    const start = new Date(startBase.getTime() + shiftMs);
    const end = new Date(start.getTime() + durationMs);

    return {
      id: idSuffix ? `${item?.id}__${idSuffix}` : item?.id,
      title: item?.title,
      start,
      end,
      contentType: ctype,
      color,
      thumbnail: item?.thumbnail || null,
      resource: item,
      isRecurring: !!isRecurring,
    };
  };

  // non-recurring
  if (!rruleString || !/FREQ=/i.test(rruleString)) {
    return [buildEvent(dtStartLocal, null, false)];
  }

  // recurring: do RRULE math in floating-UTC, then convert back to local wall-clock
  let tmp;
  try {
    tmp = RRule.fromString(rruleString);
  } catch (e) {
    console.warn("bad RRULE, fallback to single:", rruleString, e);
    return [buildEvent(dtStartLocal, null, false)];
  }

  const baseOptions = tmp.origOptions || tmp.options || {};

  let anchored;
  try {
    anchored = new RRule({
      ...baseOptions,
      dtstart: toFloatingUtcDate(dtStartLocal),
    });
  } catch (e) {
    console.warn("failed to anchor RRULE, fallback to single:", baseOptions, e);
    return [buildEvent(dtStartLocal, null, false)];
  }

  const occUtc = anchored.between(
    toFloatingUtcDate(rs),
    toFloatingUtcDate(re),
    true
  );

  const occLocal = occUtc.map(fromFloatingUtcDate);

  return occLocal.map((d) => {
    const shownStart = new Date(d.getTime() + shiftMs);
    return buildEvent(d, `${shownStart.toISOString()}_${rank}`, true);
  });
}


export function decodeMonthlyPatternFromRRule(rule) {
  const opt = rule?.options || {};

  // Case A: "on day 25"
  const md = Array.isArray(opt.bymonthday) ? opt.bymonthday[0] : opt.bymonthday;
  if (Number.isFinite(md)) {
    return { mode: "bymonthday", dayOfMonth: md };
  }

  // Case B: "on the last Wednesday" (BYDAY=WE;BYSETPOS=-1)
  const spRaw = Array.isArray(opt.bysetpos) ? opt.bysetpos[0] : opt.bysetpos;
  const sp = Number.isFinite(spRaw) ? spRaw : null;

  let bw = opt.byweekday;
  if (bw && !Array.isArray(bw)) bw = [bw];

  // Normalize weekday token from RRule weekday object(s)
  const weekdayTokenFromObj = (w) => {
    if (!w) return null;
    const idx = typeof w.weekday === "number" ? w.weekday : null; // 0..6 => MO..SU
    return ["MO", "TU", "WE", "TH", "FR", "SA", "SU"][idx] || null;
  };

  // Some rules encode "nth weekday" as Weekday.n (e.g., w.n === -1)
  // Others use BYSETPOS with plain weekday(s).
  if (Array.isArray(bw) && bw.length > 0) {
    const w0 = bw[0];
    const token = weekdayTokenFromObj(w0);

    const nFromWeekday = Number.isFinite(w0?.n) ? w0.n : null; // -1, 1, 2, 3, 4...

    // Prefer explicit BYSETPOS if present, else fallback to w.n
    const nth = sp != null ? sp : nFromWeekday;

    if (token && Number.isFinite(nth)) {
      return { mode: "nthWeekday", nth, weekdayToken: token };
    }
  }

  return { mode: "unknown" };
}